import React, { createContext, useContext } from "react";
import { normalizeShowWeekends, ShowWeekendsOption } from "helpers/calendar-weekends.helper";

export type ViewType = "dayGridMonth" | "timeGridDay" | "timeGridWeek" | "resourceTimeGridDay";

export interface CalendarSettings {
  config: {
    start: string;
    end: string;
    step: number;
    showWeekends: ShowWeekendsOption;
    isAllowedTimeLock: boolean;
    backgroundColorScheme?: string;
  };
  view: {
    view: ViewType;
    date: string;
  };
}

interface CalendarSettingsContextProps {
  calendarSettings: CalendarSettings;
  calendarViewType: ViewType;
  calendarViewDate: string;
  updateCalendarSettings: (settings: Partial<CalendarSettings>) => void;
  clearCalendarSettings: () => void;
}

const CalendarConfigContext = createContext<CalendarSettingsContextProps | null>(null);

export const useCalendarConfig = () => {
  const context = useContext(CalendarConfigContext);
  if (!context) {
    throw new Error("useCalendarConfig must be used within a UserProvider");
  }
  return context;
};

export const DEFAULT_CALENDAR_CONFIG: CalendarSettings["config"] = {
  start: "08:00",
  end: "16:00",
  step: 10,
  showWeekends: "BOTH",
  isAllowedTimeLock: false,
};

export const CalendarConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialSettings: CalendarSettings = {
    config: DEFAULT_CALENDAR_CONFIG,
    view: {
      view: "dayGridMonth",
      date: new Date().toISOString(),
    },
  };

  const loadInitialSettings = (): CalendarSettings => {
    const storedSettings = localStorage.getItem("calendarSettings");
    if (storedSettings) {
      try {
        const parsedSettings = JSON.parse(storedSettings);
        return {
          ...parsedSettings,
          config: {
            ...parsedSettings.config,
            // storedSettings may still hold the legacy boolean value cached before
            // showWeekends became a 4-option enum - normalize it so the settings
            // form has a selected option without requiring the user to log in again
            showWeekends: normalizeShowWeekends(parsedSettings.config?.showWeekends),
          },
        };
      } catch (error) {
        console.error("Failed to parse calendar settings from localStorage:", error);
      }
    }
    return initialSettings;
  };

  const [calendarSettings, setCalendarSettings] = React.useState<CalendarSettings>(loadInitialSettings);

  const updateCalendarSettings = (settings: Partial<CalendarSettings>) => {
    setCalendarSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings, ...settings };
      localStorage.setItem("calendarSettings", JSON.stringify(updatedSettings));
      return updatedSettings;
    });
  };

  const clearCalendarSettings = () => {
    localStorage.removeItem("calendarSettings");
  };

  const value: CalendarSettingsContextProps = {
    calendarSettings,
    calendarViewType: calendarSettings.view.view,
    calendarViewDate: calendarSettings.view.date,
    updateCalendarSettings,
    clearCalendarSettings
  };

  return <CalendarConfigContext.Provider value={value}>{children}</CalendarConfigContext.Provider>;
};

